import React, { useContext, useState, useEffect } from 'react';
import ReactLoading from 'react-loading';
import styled, { useTheme } from 'styled-components';
import { StyledTableContainer } from 'components/Main/table/TableStyles';
import AddCreativeModal from './AddCreativeModal';
import InputContext, { creativesDataType } from 'context/InputContext';
import { useCampaignContext } from 'context/CampaignContext';
import APIContext from 'context/APIContext';
import { API } from 'aws-amplify';
import { getPreSignedUrl } from 'graphql/queries';
import { deleteStoredQuery } from 'graphql/mutations';
import { GetPreSignedUrlQuery, S3Method, DeleteStoredQueryMutationVariables } from 'API';
import DeleteConfirmationModal, { modalObject } from 'components/DeleteConfirmationModall';
import { useStoreQueries } from 'customHooks/useStoreQueries';
import { IconButton } from 'components/style/Global';

// Styled Components
const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const HeaderTitle = styled.h1`
  font-size: 1vw;
  font-family: FontRegular;
`;

const CreativesContainer = styled.div`
  position: relative;
`;

const NavigationButton = styled.button<{ direction: 'left' | 'right' }>`
  position: absolute;
  ${(props) => props.direction}: -20px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: 32px;
  height: 32px;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 50%;
  background-color: white;
  color: ${(props) => props.theme.primaryGray};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 14px;

  &:hover {
    background-color: ${(props) => props.theme.placeholderBackground || '#f8f9fa'};
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

const CreativeCardContainer = styled.div`
  min-width: calc(25% - 0.75rem);
  max-width: calc(25% - 0.75rem);
  width: calc(25% - 0.75rem);
  flex-shrink: 0;
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 12px;
  padding: 1.25rem;
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow:
    0 2px 8px rgba(0, 0, 0, 0.08),
    0 1px 3px rgba(0, 0, 0, 0.06);
  position: relative;

  &:hover {
    .edit-btns {
      display: flex !important;
    }
  }
`;

const EditActionsContainer = styled.div`
  display: none;
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  flex-direction: row;
  gap: 5px;
`;

const EditButton = styled.div`
  font-weight: 300;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 50%;
  cursor: pointer;
  padding: 10px;
  transition: all 0.2s ease;
  background-color: ${(props) => props.theme.c50 || '#ffffff'};
  color: ${(props) => props.theme.brand || props.theme.primaryGray};
  border: 1px solid ${(props) => props.theme.c200 || props.theme.border};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;

  span {
    font-size: 18px;
  }

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.2);
  }
`;

const PreviewContainer = styled.div`
  width: 100%;
  height: 380px;
  background-color: ${(props) => props.theme.placeholderBackground || '#f8f9fa'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: ${(props) => props.theme.secondaryGray || props.theme.primaryGray};
  overflow: hidden;
  border: 1px solid ${(props) => props.theme.border};
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 2px;
`;

const PreviewVideo = styled.video`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 2px;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 2px;
`;

const PlaceholderContent = styled.div`
  text-align: center;
  color: ${(props) => props.theme.secondaryGray};
`;

const PlaceholderIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const CreativeIdText = styled.div`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) => props.theme.primaryGray};
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
  letter-spacing: 0.01em;

  span {
    font-size: 0.85rem;
    font-weight: 400;
    color: ${(props) => props.theme.secondaryGray || props.theme.primaryGray};
    opacity: 0.8;
    margin-top: 0.125rem;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto auto auto;
  gap: 0.75rem 1rem;
  font-size: 0.8rem;
  min-height: 120px;
  padding-top: 0.25rem;
`;

const MetricLabel = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
  color: ${(props) => props.theme.primaryGray};
  opacity: 0.9;
  word-break: break-all;
  white-space: normal;
  line-height: 1.3;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const MetricValue = styled.div`
  color: ${(props) => props.theme.brand || props.theme.primaryGray};
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.2;

  &:hover {
    position: relative;
  }

  &:hover::after {
    content: attr(data-full-value);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    color: ${(props) => props.theme.primaryGray};
    text-align: center;
    background-color: ${(props) => props.theme.placeholderBackground};
    /* border: 1px solid ${(props) => props.theme.c300 || props.theme.border}; */
    border-radius: 6px;
    padding: 0.5rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 500;
    white-space: nowrap;
    z-index: 1000;
    pointer-events: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

interface CreativeLibraryProps {
  dataSourceName: string;
  title?: string; // Optional title from backend
  position: string;
  data: any[]; // Dynamic data from the section
  creatives: creativesDataType[];
  storedQueries: any[];
  setStoredQueries: React.Dispatch<React.SetStateAction<any[]>>;
  readOnly?: boolean; // Optional read-only mode for unauthorized users
}

const CreativeLibrarySection: React.FC<CreativeLibraryProps> = ({
  dataSourceName,
  title,
  position,
  data,
  creatives,
  storedQueries,
  setStoredQueries,
  readOnly = false, // Default to false for backward compatibility
}) => {
  const { creativeLibrarySections, setCreativeLibrarySections } = useContext(InputContext);
  const [showAddCreativeModal, setShowAddCreativeModal] = useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalData, setDeleteModalData] = useState<modalObject | null>(null);
  const [deleteAction, setDeleteAction] = useState<'creative' | 'section'>('creative');
  const [creativeToDelete, setCreativeToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingCreative, setEditingCreative] = useState<creativesDataType | null>(null);
  const { activeCampaign } = useCampaignContext();
  const { getEnvironment } = useContext(APIContext);
  const [storeQueries] = useStoreQueries();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(title || 'title');

  // Auto-open Add Creative modal kada se kreira nova sekcija
  useEffect(() => {
    const currentSection = creativeLibrarySections.find((s) => s.position === position);
    if (currentSection?.autoOpenAddModal) {
      setShowAddCreativeModal(true);

      // Ukloni flag da se ne otvara ponovo
      const updatedSections = creativeLibrarySections.map((section) =>
        section.position === position ? { ...section, autoOpenAddModal: false } : section,
      );
      setCreativeLibrarySections(updatedSections);
    }
  }, [creativeLibrarySections, position]); // Uklonio setCreativeLibrarySections

  // Extract Creative IDs from data (combine creative_id and creative_name fields)
  const getCreativeIds = () => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const creatives: Array<{ id: string; label: string }> = [];
    const seenIds = new Set<string>();

    data.forEach((item) => {
      if (item.creative_id && !seenIds.has(item.creative_id.toString())) {
        seenIds.add(item.creative_id.toString());
        creatives.push({
          id: item.creative_id.toString(),
          label: item.creative_name || item.creative_id.toString(), // Use creative_name as label if available
        });
      }
    });

    return creatives;
  };

  // Extract available metrics from data (non-string fields excluding creative_name/creative_id)
  const getAvailableMetrics = () => {
    if (!data || !Array.isArray(data) || data.length === 0) return [];

    const firstItem = data[0];
    const metrics: Array<{ id: string; label: string }> = [];

    Object.entries(firstItem).forEach(([key, value]) => {
      // Include only numeric fields and exclude creative_name/creative_id and date fields
      if (
        typeof value === 'number' &&
        key !== 'creative_name' &&
        key !== 'creative_id' &&
        !key.toLowerCase().includes('date') &&
        key !== 'year' &&
        key !== 'month' &&
        key !== 'day'
      ) {
        metrics.push({
          id: key,
          label: key, // Capitalize first letter
        });
      }
    });

    return metrics;
  };

  const handleAddCreative = () => {
    setShowAddCreativeModal(true);
  };

  const handleDeleteCreative = (creativeId: string) => {
    const creative = creatives.find((c) => c.id === creativeId);
    setCreativeToDelete(creativeId);
    setDeleteAction('creative');
    setDeleteModalData({
      title: 'Delete Creative',
      message: `Are you sure you want to delete creative "${creative?.creativeName || creative?.creativeID}"? This action cannot be undone.`,
      mainButton: 'Delete',
      secondaryButton: 'Cancel',
    });
    setShowDeleteModal(true);
  };

  const handleDeleteSection = () => {
    setDeleteAction('section');
    setDeleteModalData({
      title: 'Delete Creative Library Section',
      message: `Are you sure you want to delete the entire "${title || dataSourceName}" section? This will remove all ${creatives.length} creative(s) in this section. This action cannot be undone.`,
      mainButton: 'Delete Section',
      secondaryButton: 'Cancel',
    });
    setShowDeleteModal(true);
  };

  const handleTitleEdit = () => {
    if (activeCampaign?.authorized) {
      setIsEditingTitle(true);
      setTempTitle(title || 'title');
    }
  };

  const handleTitleSave = async () => {
    if (!tempTitle.trim()) {
      setTempTitle(title || 'title');
      setIsEditingTitle(false);
      return;
    }

    try {
      // Update backend first
      const storeQueriesVariables = {
        campaign_id: activeCampaign?.campaignId?.toString() ?? '',
        position: position,
        query_id: dataSourceName,
        title: tempTitle.trim(),
        creatives: creatives.map((creative) => ({
          id: creative.id,
          position: creative.position,
          creativeOutputLocation: creative.file,
          creativeID: creative.creativeID,
          creativeName: creative.creativeName,
          metrics: creative.metrics,
        })),
      };

      const result = await storeQueries(storeQueriesVariables);

      if (result === '200') {
        // Update local state
        setCreativeLibrarySections(
          creativeLibrarySections.map((section) =>
            section.position === position ? { ...section, title: tempTitle.trim() } : section,
          ),
        );
        setIsEditingTitle(false);
      } else {
        console.error('❌ Failed to update title:', result);
        alert('Failed to update title. Please try again.');
        setTempTitle(title || 'title');
      }
    } catch (error) {
      console.error('❌ Error updating title:', error);
      alert('Error updating title. Please try again.');
      setTempTitle(title || 'title');
    }
  };

  const handleTitleCancel = () => {
    setTempTitle(title || 'title');
    setIsEditingTitle(false);
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    if (!confirmed) {
      setShowDeleteModal(false);
      setCreativeToDelete(null);
      return;
    }

    setIsDeleting(true);

    try {
      if (deleteAction === 'creative' && creativeToDelete) {
        // Prepare updated sections without the creative
        const updatedSections = creativeLibrarySections.map((section) => {
          if (section.position === position) {
            const updatedCreatives = section.creatives.filter((c) => c.id !== creativeToDelete);
            return { ...section, creatives: updatedCreatives };
          }
          return section;
        });

        const currentSection = updatedSections.find((section) => section.position === position);
        if (!currentSection) {
          alert('Error: Could not find section to update.');
          return;
        }

        const storeQueriesVariables = {
          campaign_id: activeCampaign?.campaignId?.toString() ?? '',
          position: position,
          query_id: dataSourceName,
          creatives: currentSection.creatives.map((creative) => ({
            id: creative.id,
            position: creative.position,
            creativeOutputLocation: creative.file,
            creativeID: creative.creativeID,
            creativeName: creative.creativeName,
            metrics: creative.metrics,
          })),
        };

        const result = await storeQueries(storeQueriesVariables);

        if (result === '200') {
          setCreativeLibrarySections(updatedSections);
        } else {
          alert(`Failed to delete creative. Backend returned: ${result}`);
        }
      } else if (deleteAction === 'section') {
        const deleteVariables: DeleteStoredQueryMutationVariables = {
          campaign_id: activeCampaign?.campaignId?.toString() ?? '',
          Environment: getEnvironment,
          placeholder: position,
        };

        const result = (await API.graphql({
          query: deleteStoredQuery,
          variables: deleteVariables,
          authMode: 'AMAZON_COGNITO_USER_POOLS',
        })) as any;

        const deleteResponse = result?.data?.deleteStoredQuery;
        const statusCode = deleteResponse?.StatusCode;
        const message = deleteResponse?.Message;

        // More flexible success check
        const isSuccess =
          statusCode === '200' ||
          statusCode === 200 ||
          (message &&
            (message.toLowerCase().includes('success') ||
              message.toLowerCase().includes('deleted') ||
              message.toLowerCase().includes('removed')));

        if (isSuccess) {
          const updatedSections = creativeLibrarySections.filter((section) => section.position !== position);
          setCreativeLibrarySections(updatedSections);

          // IMPORTANT: Also remove from storedQueries to prevent useEffect from reloading it
          const updatedStoredQueries = storedQueries.filter((query) => query?.position !== position);
          setStoredQueries(updatedStoredQueries);
        } else {
          alert(`Failed to delete section. Status: ${statusCode}, Message: ${message}`);
        }
      }
    } catch (error) {
      alert('An unexpected error occurred while deleting. Please try again.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setCreativeToDelete(null);
    }
  };

  const handleCreativeSubmit = async (creativeData: any) => {
    if (editingCreative) {
      // Edit mode - update existing creative

      // Extract metrics from creativeData
      const metrics: Array<{ position: number; label: string; value: number; column?: string; decimals?: number }> = [];

      // Get data from the section that matches this creative ID
      const matchingDataItem = data.find((item) => item.creative_id === creativeData.creativeID);

      // Add selected data metrics (now using availableMetricsObjects if available)
      if (creativeData.availableMetricsObjects && Array.isArray(creativeData.availableMetricsObjects)) {
        creativeData.availableMetricsObjects.forEach((metricObj: any) => {
          metrics.push({
            position: metricObj.position,
            label: metricObj.label,
            value: metricObj.value,
            column: metricObj.column,
            decimals: metricObj.decimals,
          });
        });
      } else if (creativeData.selectedMetrics && Array.isArray(creativeData.selectedMetrics)) {
        // Fallback for backward compatibility
        creativeData.selectedMetrics.forEach((metricKey: string, index: number) => {
          if (matchingDataItem && matchingDataItem[metricKey] !== undefined) {
            metrics.push({
              position: index + 1,
              label: metricKey,
              value: matchingDataItem[metricKey],
              column: metricKey,
              decimals: 2,
            });
          }
        });
      }

      // Add custom metrics if they exist in creativeData
      if (creativeData.customMetrics && Array.isArray(creativeData.customMetrics)) {
        const basePosition = creativeData.availableMetricsObjects?.length || creativeData.selectedMetrics?.length || 0;
        creativeData.customMetrics.forEach((customMetric: any, index: number) => {
          metrics.push({
            position: basePosition + index + 1,
            label: customMetric.label,
            value: customMetric.value,
            column: undefined,
            decimals: 2,
          });
        });
      }

      const updatedCreative: creativesDataType = {
        ...editingCreative,
        creativeName: creativeData.creativeName || editingCreative.creativeName,
        file: creativeData.outputLocation || editingCreative.file,
        fileType: creativeData.fileType || editingCreative.fileType,
        creativeID: creativeData.creativeID, // Allow Creative ID change
        metrics: metrics,
      };

      try {
        // First update backend using storeQueries hook

        // Prepare all creatives for this section with the updated creative
        const updatedCreatives = creatives.map((creative) =>
          creative.id === editingCreative.id ? updatedCreative : creative,
        );

        const storeQueriesVariables = {
          campaign_id: activeCampaign?.campaignId?.toString() ?? '',
          position: position,
          query_id: dataSourceName,
          creatives: updatedCreatives.map((creative) => ({
            id: creative.id,
            position: creative.position,
            creativeOutputLocation: creative.file,
            creativeID: creative.creativeID,
            creativeName: creative.creativeName,
            metrics: creative.metrics,
          })),
        };

        const result = await storeQueries(storeQueriesVariables);

        if (result === '200') {
          // Now update local state
          setCreativeLibrarySections(
            creativeLibrarySections.map((section) =>
              section.position === position
                ? {
                    ...section,
                    creatives: section.creatives.map((creative) =>
                      creative.id === editingCreative.id ? updatedCreative : creative,
                    ),
                  }
                : section,
            ),
          );

          setEditingCreative(null); // Clear edit state
        } else {
          alert(`Failed to update creative. Backend returned: ${result}`);
          return; // Don't close modal or clear state on failure
        }
      } catch (error) {
        alert('Error updating creative. Please try again.');
        return; // Don't close modal or clear state on failure
      }
    } else {
      // Add mode - create new creative

      // Extract metrics from creativeData - AddCreativeModal should provide these
      // AddCreativeModal creates combined metrics array (data + custom metrics)
      const metrics: Array<{ position: number; label: string; value: number; column?: string; decimals?: number }> = [];

      // Get data from the section that matches this creative ID
      const matchingDataItem = data.find((item) => item.creative_id === creativeData.creativeID);

      // Add selected data metrics (now using availableMetricsObjects if available)
      if (creativeData.availableMetricsObjects && Array.isArray(creativeData.availableMetricsObjects)) {
        creativeData.availableMetricsObjects.forEach((metricObj: any) => {
          metrics.push({
            position: metricObj.position,
            label: metricObj.label,
            value: metricObj.value,
            column: metricObj.column,
            decimals: metricObj.decimals,
          });
        });
      } else if (creativeData.selectedMetrics && Array.isArray(creativeData.selectedMetrics)) {
        // Fallback for backward compatibility
        creativeData.selectedMetrics.forEach((metricKey: string, index: number) => {
          if (matchingDataItem && matchingDataItem[metricKey] !== undefined) {
            metrics.push({
              position: index + 1,
              label: metricKey,
              value: matchingDataItem[metricKey],
              column: metricKey,
              decimals: 2,
            });
          }
        });
      }

      // Add custom metrics if they exist in creativeData
      if (creativeData.customMetrics && Array.isArray(creativeData.customMetrics)) {
        const basePosition = creativeData.availableMetricsObjects?.length || creativeData.selectedMetrics?.length || 0;
        creativeData.customMetrics.forEach((customMetric: any, index: number) => {
          metrics.push({
            position: basePosition + index + 1,
            label: customMetric.label,
            value: customMetric.value,
            column: undefined,
            decimals: 2,
          });
        });
      }

      // Create new creative with backend format (S3 file name instead of blob URL)
      // Generate unique ID by finding the next available number
      const existingIds = creatives
        .map((c) => {
          const idParts = c.id.split('.');
          return idParts.length > 1 ? parseInt(idParts[1]) : 0;
        })
        .filter((num) => !isNaN(num));

      // Find next available ID number
      let nextIdNumber = 0;
      while (existingIds.includes(nextIdNumber)) {
        nextIdNumber++;
      }

      const newCreative: creativesDataType = {
        id: `${position}.${nextIdNumber}`, // Library section position + next available number (e.g., "901.0", "901.1")
        position: nextIdNumber + 1,
        file: creativeData.outputLocation || '', // Use S3 file name from backend instead of blob URL
        fileType: creativeData.fileType || 'application/octet-stream', // MIME type from upload
        creativeID: creativeData.creativeID,
        creativeName: creativeData.creativeName || creativeData.creativeID,
        metrics: metrics, // Include all metrics (data + custom)
      };

      // Update the section's creatives in local state
      setCreativeLibrarySections(
        creativeLibrarySections.map((section) =>
          section.position === position ? { ...section, creatives: [...section.creatives, newCreative] } : section,
        ),
      );
    }

    setShowAddCreativeModal(false);
  };

  const handleEditCreative = (creative: creativesDataType) => {
    // Create a deep copy of the creative to avoid mutating the original
    const creativeCopy = JSON.parse(JSON.stringify(creative));
    setEditingCreative(creativeCopy);
    setShowAddCreativeModal(true);
  };

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320; // Width of one card + gap
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount;

      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth',
      });
    }
  };

  return (
    <StyledTableContainer expand={0}>
      {/* Header */}
      <HeaderContainer>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {isEditingTitle && !readOnly ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="text"
                value={tempTitle}
                onChange={(e) => setTempTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleTitleSave();
                  if (e.key === 'Escape') handleTitleCancel();
                }}
                style={{
                  fontSize: '1vw',
                  fontFamily: 'FontRegular',
                  border: '1px solid #ccc',
                  padding: '0.25rem',
                  borderRadius: '4px',
                  minWidth: '200px',
                }}
                autoFocus
              />
              <IconButton type="button" className="titleSave" onClick={handleTitleSave}>
                Save
              </IconButton>
              <IconButton type="button" onClick={handleTitleCancel}>
                Cancel
              </IconButton>
            </div>
          ) : (
            <>
              {
                title ? (
                  // Ako postoji title, prikaži ga
                  <>
                    <HeaderTitle>{title}</HeaderTitle>
                    {activeCampaign?.authorized && !readOnly && (
                      <IconButton
                        type="button"
                        onClick={handleTitleEdit}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '0.25rem',
                          opacity: 0.7,
                        }}
                        title="Edit title"
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                          edit
                        </span>
                      </IconButton>
                    )}
                  </>
                ) : // Ako nema title
                activeCampaign?.authorized && !readOnly ? (
                  // Ako je authorized, prikaži "title" string i edit gumb
                  <>
                    <HeaderTitle>title</HeaderTitle>
                    <IconButton
                      type="button"
                      onClick={handleTitleEdit}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.25rem',
                        opacity: 0.7,
                      }}
                      title="Edit title"
                    >
                      <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>
                        edit
                      </span>
                    </IconButton>
                  </>
                ) : null // Ako nije authorized, ne prikazuj ništa
              }
            </>
          )}
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <button type="button" onClick={handleAddCreative}>
              + Add New Creative
            </button>
            <IconButton
              title="Delete Creative Section"
              style={{ marginLeft: '5px' }}
              type="button"
              onClick={handleDeleteSection}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span style={{ fontSize: '16px' }} className="material-symbols-outlined">
                  hourglass_empty
                </span>
              ) : (
                <span style={{ fontSize: '16px' }} className="material-symbols-outlined">
                  delete
                </span>
              )}
            </IconButton>
          </div>
        )}
      </HeaderContainer>

      {/* Creatives Grid with Navigation */}
      <CreativesContainer>
        {/* Left Arrow */}
        {creatives.length > 4 && (
          <NavigationButton type="button" direction="left" onClick={() => scroll('left')}>
            <span className="material-symbols-outlined">chevron_left</span>
          </NavigationButton>
        )}

        {/* Right Arrow */}
        {creatives.length > 4 && (
          <NavigationButton type="button" direction="right" onClick={() => scroll('right')}>
            <span className="material-symbols-outlined">chevron_right</span>
          </NavigationButton>
        )}

        {/* Scroll Container */}
        <ScrollContainer ref={scrollContainerRef}>
          {creatives.length > 0 ? (
            creatives.map((creative) => (
              <CreativeCard
                key={creative.id}
                creative={creative}
                onDelete={() => handleDeleteCreative(creative.id)}
                onEdit={() => handleEditCreative(creative)}
                readOnly={readOnly}
              />
            ))
          ) : (
            <></>
          )}
        </ScrollContainer>
      </CreativesContainer>

      {/* Add Creative Modal */}
      <AddCreativeModal
        isOpen={showAddCreativeModal}
        onClose={() => {
          setShowAddCreativeModal(false);
          setEditingCreative(null); // Clear edit state when closing
        }}
        onConfirm={handleCreativeSubmit}
        availableCreativeIds={getCreativeIds()}
        availableMetrics={getAvailableMetrics()}
        sectionPosition={position}
        existingCreatives={creatives}
        editingCreative={editingCreative}
        isEditMode={!!editingCreative}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteModalData && (
        <DeleteConfirmationModal handleDeleteConfirmationModal={handleConfirmDelete} modalObject={deleteModalData} />
      )}
    </StyledTableContainer>
  );
};

const CreativeCard: React.FC<{
  creative: creativesDataType;
  onDelete: () => void;
  onEdit: () => void;
  readOnly?: boolean;
}> = ({ creative, onDelete, onEdit, readOnly = false }) => {
  const [isLoading, setIsLoading] = useState(
    !creative.file || (!creative.file.startsWith('blob:') && !creative.file.startsWith('data:')),
  );
  const [fileUrl, setFileUrl] = useState<string>(
    creative.file.startsWith('blob:') || creative.file.startsWith('data:') ? creative.file : '',
  );
  const { activeCampaign } = useCampaignContext();
  const { getEnvironment } = useContext(APIContext);
  const theme = useTheme();

  // Load file URL if it's a backend file reference (S3 key)
  React.useEffect(() => {
    const loadFileUrl = async () => {
      if (creative.file && !creative.file.startsWith('blob:') && !creative.file.startsWith('data:')) {
        // This is a backend S3 file reference, get presigned URL
        try {
          const preSignedURL = (await API.graphql({
            query: getPreSignedUrl,
            variables: {
              ClientMethod: S3Method.get_object,
              Client: {
                code: activeCampaign?.ClientCode,
                country: activeCampaign?.ClientCountry,
              },
              Environment: getEnvironment,
              FileName: creative.file,
            },
            authMode: 'AMAZON_COGNITO_USER_POOLS',
          })) as { data: GetPreSignedUrlQuery };

          const url = preSignedURL.data.getPreSignedUrl?.url;
          if (url) {
            setFileUrl(url);
            setIsLoading(false);
          } else {
            throw new Error('No URL returned from presigned request');
          }
        } catch (error) {
          setIsLoading(false);
        }
      }
    };

    loadFileUrl();
  }, [creative.file, activeCampaign, getEnvironment]);

  // Helper function to get clean filename from creative name or output location
  const getCleanFileName = () => {
    if (creative.creativeName && creative.creativeName !== creative.creativeID) {
      return creative.creativeName;
    }
    // If we have backend data, try to extract filename from output location
    // This assumes output location format: "uuid_filename.ext"
    const underscoreIndex = creative.file.indexOf('_');
    if (underscoreIndex > 0) {
      return creative.file.substring(underscoreIndex + 1);
    }
    return creative.creativeID;
  };

  // Render file preview based on type
  const renderFilePreview = () => {
    if (!fileUrl) {
      if (isLoading) {
        return (
          <PlaceholderContent>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                margin: 'auto',
              }}
            >
              <ReactLoading
                type="spinningBubbles"
                color={theme.publishButton || theme.primaryGray}
                width={40}
                height={40}
              />
            </div>
          </PlaceholderContent>
        );
      }
      return (
        <PlaceholderContent>
          <PlaceholderIcon>📄</PlaceholderIcon>
          <div>{getCleanFileName()}</div>
        </PlaceholderContent>
      );
    }

    // Check MIME type to determine file type
    if (creative.fileType && creative.fileType.startsWith('image/')) {
      return (
        <PreviewImage
          src={fileUrl}
          alt="Creative preview"
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
        />
      );
    }

    if (creative.fileType && creative.fileType.startsWith('video/')) {
      return (
        <PreviewVideo controls muted onLoadedData={() => setIsLoading(false)} onError={() => setIsLoading(false)}>
          <source src={fileUrl} />
        </PreviewVideo>
      );
    }

    // Check for HTML files (banneri) - provjeri po MIME tipu ili po ekstenziji

    const fileName = creative.file || '';
    const isHtmlFile =
      (creative.fileType && creative.fileType.startsWith('text/html')) ||
      fileName.toLowerCase().endsWith('.html') ||
      fileName.toLowerCase().endsWith('.htm');

    if (isHtmlFile) {
      return (
        <PreviewIframe
          src={fileUrl}
          title="HTML Banner Preview"
          onLoad={() => setIsLoading(false)}
          onError={() => setIsLoading(false)}
        />
      );
    }

    // For other file types (PDF, documents, etc.)
    return (
      <PlaceholderContent>
        <PlaceholderIcon>📄</PlaceholderIcon>
        <div>{getCleanFileName()}</div>
        <div style={{ fontSize: '0.7rem', opacity: 0.7 }}>
          {creative.fileType?.split('/')[1]?.toUpperCase() || 'FILE'}
        </div>
      </PlaceholderContent>
    );
  };

  return (
    <CreativeCardContainer>
      {/* Edit/Delete Actions - Hidden by default, shown on hover - Only for authorized users */}
      {!readOnly && (
        <EditActionsContainer className="edit-btns">
          <EditButton onClick={onEdit}>
            <span className="material-symbols-outlined">edit</span>
          </EditButton>
          <EditButton onClick={onDelete}>
            <span className="material-symbols-outlined">delete</span>
          </EditButton>
        </EditActionsContainer>
      )}

      {/* Creative Image/Video Preview */}
      <PreviewContainer>{renderFilePreview()}</PreviewContainer>

      {/* Creative ID */}
      <CreativeIdText title={`${creative.creativeName} (${creative.creativeID})`}>
        {creative.creativeName}
        {creative.creativeID.includes(',') ? null : <span>ID:{creative.creativeID}</span>}
      </CreativeIdText>

      {/* Metrics Grid */}
      <MetricsGrid>
        {creative.metrics.map((metric, index) => {
          const decimals = metric.decimals ?? 2;
          // Use compact notation only if the number has more than 4 digits
          const useCompact = Math.abs(metric.value) >= 10000;
          const formattedValue = new Intl.NumberFormat('en-DK', {
            notation: useCompact ? 'compact' : 'standard',
            maximumFractionDigits: decimals,
          }).format(metric.value);
          const fullValue = new Intl.NumberFormat('en-DK', {
            maximumFractionDigits: decimals,
          }).format(metric.value);

          return (
            <div key={index}>
              <MetricLabel>{metric.label}</MetricLabel>
              <MetricValue data-full-value={fullValue}>{formattedValue}</MetricValue>
            </div>
          );
        })}
      </MetricsGrid>
    </CreativeCardContainer>
  );
};

export default CreativeLibrarySection;
